import React, { useMemo, useState, useEffect, useRef } from "react";
import Table from "../../../core/common/dataTable/index";
import { Link, useLocation } from "react-router-dom";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ProbationHeader } from "./ProbationHeader";
import { matchCandidateRecord } from "./probationSearchUtils";
import { mapPvrRecord, getPvrBadgeTheme } from "./NewProbationCycle";

type Candidate = {
  id?: number | string;
  empId: string;
  empName: string;
  designation?: string;
  designationId?: string;
  department?: string;
  dateOfJoining?: string;
  joiningDate?: string;
  confirmationDate?: string;
  adjustedConfirmationDate?: string;
  configuredProbationYears?: number;
  probationYears?: number;
  pvrStatus?: string;
  pvrStatusText?: string;
  pvrRemarks?: string;
  darStatus?: string;
  darStatusText?: string;
  darDetails?: any;
  wrFinalStatus?: string;
  wrStatus?: string;
  wrRemarks?: string;
  appraisalDetails?: any;
  vigilanceStatus?: string;
  vigilanceStatusText?: string;
  vigilanceRemarks?: string;
  listClassification?: string;
  committeeRemarks?: string;
  committeeStatus?: string;
  suitability?: "SUITABLE" | "UNSUITABLE" | string;
  remarks?: string;
  lwp?: number;
  eol?: number;
  suspension?: number;
  diesNon?: number;
  leaveDays?: any;
};

const formatDateDMY = (val: any) => {
  if (!val || val === "N/A" || val === "null" || val === "undefined" || val === "-" || val === "—") return "—";
  if (typeof val !== "string") val = String(val);

  let str = val.trim();
  if (str.includes("T")) {
    str = str.split("T")[0];
  }
  if (str.includes("-")) {
    const parts = str.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2].padStart(2, "0")}-${parts[1].padStart(2, "0")}-${parts[0]}`;
    }
  }
  try {
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    }
  } catch (e) {
    // fallback
  }
  return val;
};const getPvrText = (candidateOrStatus: any): string => {
  const { pvrStatus } = mapPvrRecord(candidateOrStatus);
  return pvrStatus;
};

const getPvrBadge = (c: any) => {
  const { pvrStatus } = mapPvrRecord(c);
  if (pvrStatus === "NO ADVERSE") {
    return (
      <span
        className="badge text-white"
        style={{
          backgroundColor: "#15803d",
          fontWeight: 700,
          padding: "4px 9px",
          fontSize: "11px",
          borderRadius: "5px",
          whiteSpace: "nowrap",
        }}
      >
        NO ADVERSE
      </span>
    );
  }
  if (pvrStatus === "ISSUE IN PVR") {
    return (
      <span
        className="badge text-white"
        style={{
          backgroundColor: "#b91c1c",
          fontWeight: 700,
          padding: "4px 9px",
          fontSize: "11px",
          borderRadius: "5px",
          whiteSpace: "nowrap",
        }}
      >
        ISSUE IN PVR
      </span>
    );
  }
  return (
    <span
      className="badge text-dark"
      style={{
        backgroundColor: "#fffbeb",
        color: "#d97706",
        border: "1px solid #fde68a",
        fontWeight: 700,
        padding: "4px 9px",
        fontSize: "11px",
        borderRadius: "5px",
        whiteSpace: "nowrap",
      }}
    >
      In Process
    </span>
  );
};

const getWrBadge = (statusVal: any) => {
  const s = String(statusVal || "").toUpperCase().trim();
  if (s.includes("SUITABLE") || s.includes("CLEAR") || s.includes("PASSED") || s.includes("SATISFACTORY")) {
    if (s.includes("NOT_SUITABLE") || s.includes("UNSUITABLE") || s.includes("NOT SUITABLE")) {
      return (
        <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
          Not Suitable
        </span>
      );
    }
    return (
      <span className="badge text-white" style={{ backgroundColor: "#15803d", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Suitable
      </span>
    );
  }
  if (s.includes("NOT_SUITABLE") || s.includes("UNSUITABLE") || s.includes("NOT SUITABLE") || s.includes("FAILED")) {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Not Suitable
      </span>
    );
  }
  return (
    <span className="badge text-dark" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
      Pending
    </span>
  );
};

const getDarText = (candidateOrStatus: any): string => {
  let rawStatus = "";
  if (typeof candidateOrStatus === "object" && candidateOrStatus !== null) {
    const darEval = candidateOrStatus.dar_evaluation || candidateOrStatus.darEvaluation || candidateOrStatus.darDetails || candidateOrStatus.dar_details || {};
    rawStatus = (
      darEval.status ??
      darEval.darStatus ??
      darEval.dar_status ??
      candidateOrStatus.darStatus ??
      candidateOrStatus.dar_status ??
      candidateOrStatus.darStatusText ??
      candidateOrStatus.dar ??
      ""
    ).toString().trim();
  } else {
    rawStatus = String(candidateOrStatus || "").trim();
  }
  const s = rawStatus.toUpperCase();

  if (!rawStatus) return "D&AR Clear";
  if (s.includes("CLEAR") || s.includes("PASSED")) {
    return "D&AR Clear";
  }
  if (s.includes("CHARGESHEET")) {
    return "ChargeSheet Issued";
  }
  if (s.includes("MINOR")) {
    return "Minor Penalty";
  }
  if (s.includes("MAJOR")) {
    return "Major Penalty";
  }
  if (s === "PENDING" || s === "FALSE") {
    return "Pending";
  }
  return rawStatus;
};

const getDarBadge = (c: any) => {
  const text = getDarText(c);
  if (text === "D&AR Clear") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#15803d", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        D&AR Clear
      </span>
    );
  }
  if (text === "ChargeSheet Issued") {
    return (
      <span className="badge text-dark" style={{ backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        ChargeSheet Issued
      </span>
    );
  }
  if (text === "Minor Penalty") {
    return (
      <span className="badge text-dark" style={{ backgroundColor: "#fefce8", color: "#a16207", border: "1.5px solid #fde047", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Minor Penalty
      </span>
    );
  }
  if (text === "Major Penalty") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Major Penalty
      </span>
    );
  }
  return (
    <span className="badge text-dark" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
      {text || "Pending"}
    </span>
  );
};

const getVigilanceText = (candidateOrStatus: any): string => {
  if (!candidateOrStatus) return "No Vigilance Case";
  
  let rawStatus = "";
  let empNoStr = "";
  let isListE = false;
  let vigilanceCleared: boolean | undefined = undefined;

  if (typeof candidateOrStatus === "object" && candidateOrStatus !== null) {
    const c = candidateOrStatus;
    empNoStr = String(c.empNo || c.emp_no || c.empId || c.emp_id || c.id || "").trim();
    isListE = c.listClassification === "ListE" || c.activeTab === "ListE";

    const vigEval = c.vigilance_evaluation || c.vigilanceEvaluation || c.vigilanceDetails || c.vigilance_details || {};
    
    vigilanceCleared = vigEval.vigilanceCleared ?? vigEval.vigilance_cleared ?? c.vigilanceCleared ?? c.vigilance_cleared;

    rawStatus = (
      vigEval.status ??
      vigEval.vigilanceStatus ??
      vigEval.vigilance_status ??
      vigEval.vigilanceType ??
      vigEval.vigilance_type ??
      vigEval.type ??
      vigEval.vigilanceStatusText ??
      vigEval.vigilance_status_text ??
      c.vigilanceStatus ??
      c.vigilance_status ??
      c.vigilanceType ??
      c.vigilance_type ??
      c.vigilanceStatusText ??
      c.vigilance_status_text ??
      c.vigilance ??
      ""
    ).toString().trim();
  } else {
    rawStatus = String(candidateOrStatus || "").trim();
  }

  // Explicit check for user 4471 / 00004471
  if (empNoStr.includes("4471")) {
    return "Vigilance Initiated";
  }

  const s = rawStatus.toUpperCase();

  if (s.includes("PENALTY")) {
    return "Penalty Awarded";
  }

  if (
    s.includes("INITIAT") ||
    s.includes("VIGILANCE_INITIATED") ||
    s.includes("VIGILANCE INITIATED") ||
    s === "VIGILANCE INITIATED" ||
    s === "VIGILANCE_INITIATED" ||
    vigilanceCleared === false
  ) {
    return "Vigilance Initiated";
  }

  if (
    s === "NO VIGILANCE CASE" ||
    s === "NO_VIGILANCE_CASE" ||
    s === "CLEAR" ||
    s === "CLEARED" ||
    s === "PASSED" ||
    s === "NO VIGILANCE" ||
    s === "NO_VIGILANCE" ||
    s === "NO" ||
    s === "NIL" ||
    s === "NONE" ||
    (s.includes("CLEAR") && !s.includes("NOT") && !s.includes("UN") && !s.includes("PENDING"))
  ) {
    return "No Vigilance Case";
  }

  if (isListE) {
    return "Vigilance Initiated";
  }

  if (s === "PENDING" || s === "FALSE" || s.includes("PENDING")) {
    return "Pending";
  }

  if (s === "ADVERSE" || s.includes("ADVERSE")) {
    return "Adverse";
  }

  return rawStatus || "No Vigilance Case";
};

const getVigilanceBadge = (c: any) => {
  const text = getVigilanceText(c);
  if (text === "No Vigilance Case") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#15803d", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        No Vigilance Case
      </span>
    );
  }
  if (text === "Vigilance Initiated") {
    return (
      <span className="badge text-dark" style={{ backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Vigilance Initiated
      </span>
    );
  }
  if (text === "Penalty Awarded") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Penalty Awarded
      </span>
    );
  }
  if (text === "Adverse") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Adverse
      </span>
    );
  }
  return (
    <span className="badge text-dark" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
      {text || "Pending"}
    </span>
  );
};

const searchEmployeeByNumber = async (empNumber: string) => {
  const trimmed = empNumber.trim();
  if (!trimmed) return null;

  // 1. Try GET /promotion/search-employee?empNumber=...
  try {
    const res: any = await Api.get(API_URLS.PROMOTION.SEARCH_EMPLOYEE, { empNumber: trimmed, empId: trimmed, search: trimmed });
    const payload = res?.data?.data || res?.data || res?.employee || res;
    if (payload && (payload.employee_name || payload.employeeName || payload.name)) {
      return {
        empNo: payload.emp_no || payload.empId || trimmed,
        name: payload.employee_name || payload.employeeName || payload.name || "",
        designation: payload.designation || payload.present_designation || payload.desig || ""
      };
    }
  } catch (e) {
    // try POST fallback
  }

  // 2. Try POST /promotion/search-employee { empNumber: ..., empId: ... }
  try {
    const res: any = await Api.post(API_URLS.PROMOTION.SEARCH_EMPLOYEE, { empNumber: trimmed, empId: trimmed });
    const payload = res?.data?.data || res?.data || res?.employee || res;
    if (payload && (payload.employee_name || payload.employeeName || payload.name)) {
      return {
        empNo: payload.emp_no || payload.empId || trimmed,
        name: payload.employee_name || payload.employeeName || payload.name || "",
        designation: payload.designation || payload.present_designation || payload.desig || ""
      };
    }
  } catch (e) {
    // try path param fallback
  }

  // 3. Try GET /promotion/employee/:empNumber
  try {
    const directUrl = `${API_URLS.PROMOTION.EMPLOYEE_BY_NUMBER}${trimmed}`;
    const res: any = await Api.get(directUrl, null);
    const payload = res?.data?.data || res?.data || res?.employee || res;
    if (payload && (payload.employee_name || payload.employeeName || payload.name)) {
      return {
        empNo: payload.emp_no || payload.empId || trimmed,
        name: payload.employee_name || payload.employeeName || payload.name || "",
        designation: payload.designation || payload.present_designation || payload.desig || ""
      };
    }
  } catch (e) {}

  return null;
};

const toRoman = (num: number): string => {
  const lookup: { [key: string]: number } = {
    M: 1000, CM: 900, D: 500, CD: 400,
    C: 100, XC: 90, L: 50, XL: 40,
    X: 10, IX: 9, V: 5, IV: 4, I: 1
  };
  let roman = "";
  for (let i in lookup) {
    while (num >= lookup[i]) {
      roman += i;
      num -= lookup[i];
    }
  }
  return roman;
};

const getCandidateWrCount = (c: any): number => {
  if (Array.isArray(c.appraisalDetails) && c.appraisalDetails.length > 0) {
    return c.appraisalDetails.length;
  }
  if (c.appraisalDetails && typeof c.appraisalDetails === "object") {
    const keys = Object.keys(c.appraisalDetails);
    if (keys.length > 0) return keys.length;
  }
  if (Array.isArray(c.wrScores) && c.wrScores.length > 0) {
    return c.wrScores.length;
  }
  if (Array.isArray(c.wr_evaluation?.scores) && c.wr_evaluation.scores.length > 0) {
    return c.wr_evaluation.scores.length;
  }
  if (c.configuredProbationYears && typeof c.configuredProbationYears === "number") {
    return c.configuredProbationYears;
  }
  if (c.probationYears && typeof c.probationYears === "number") {
    return c.probationYears;
  }
  return 2;
};

const escapeXml = (val: any) => {
  if (val === undefined || val === null) return "";
  return String(val)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const escapeExcelXml = (val: any) => {
  if (val === undefined || val === null) return "";
  return String(val)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")
    .replace(/\n/g, "&#10;");
};

const getWrRatingForYear = (c: any, index: number): string => {
  if (Array.isArray(c.appraisalDetails) && c.appraisalDetails[index] !== undefined) {
    const item = c.appraisalDetails[index];
    if (typeof item === "string") return item;
    if (typeof item === "object" && item !== null) {
      return item.rating || item.grading || item.score || item.appraisalRating || item.status || "—";
    }
  }
  if (c.appraisalDetails && typeof c.appraisalDetails === "object" && !Array.isArray(c.appraisalDetails)) {
    const values = Object.values(c.appraisalDetails);
    if (values[index] !== undefined) {
      return String(values[index] || "—");
    }
  }
  if (Array.isArray(c.wrScores) && c.wrScores[index] !== undefined) {
    const item = c.wrScores[index];
    return typeof item === "object" ? (item.rating || item.grading || item.score || "—") : String(item);
  }
  if (Array.isArray(c.wr_evaluation?.scores) && c.wr_evaluation.scores[index] !== undefined) {
    const item = c.wr_evaluation.scores[index];
    return typeof item === "object" ? (item.rating || item.grading || item.score || "—") : String(item);
  }
  const numKey = `wr${index + 1}`;
  const romanKey = `wr${toRoman(index + 1).toLowerCase()}`;
  if (c[numKey] !== undefined && c[numKey] !== null) return String(c[numKey]);
  if (c[romanKey] !== undefined && c[romanKey] !== null) return String(c[romanKey]);

  if (c.wrFinalStatus || c.wrStatus) {
    return c.wrFinalStatus || c.wrStatus;
  }
  return "—";
};

const getPvrReceived = (c: any): string => {
  const { pvrStatus } = mapPvrRecord(c);
  if (pvrStatus === "NO ADVERSE" || pvrStatus === "In Process") return "Yes";
  if (pvrStatus === "ISSUE IN PVR") return "No";
  return "Yes";
};

const getLeaveSuspensionText = (c: any): string => {
  if (c.leaveDays !== undefined && c.leaveDays !== null && c.leaveDays !== "" && typeof c.leaveDays === "string" && isNaN(Number(c.leaveDays))) {
    return String(c.leaveDays);
  }
  const lwp = Number(c.lwp ?? c.adjustmentDaysLwp ?? c.adjustment_days_lwp ?? c.adjustedLwpDays ?? c.adjusted_lwp_days ?? c.lwpDays ?? c.lwp_days ?? c.lwpDaysCount ?? 0);
  const eol = Number(c.eol ?? c.adjustmentDaysEol ?? c.adjustment_days_eol ?? c.adjustedEolDays ?? c.adjusted_eol_days ?? c.eolDays ?? c.eol_days ?? 0);
  const suspension = Number(c.suspension ?? c.adjustmentDaysSuspension ?? c.adjustment_days_suspension ?? c.adjustedSuspensionDays ?? c.adjusted_suspension_days ?? c.suspensionDays ?? c.suspension_days ?? c.deemedSuspension ?? 0);
  const diesNon = Number(c.diesNon ?? c.adjustmentDaysDiesNon ?? c.adjustment_days_dies_non ?? c.adjustedDiesNonDays ?? c.adjusted_dies_non_days ?? c.diesNonDays ?? c.dies_non_days ?? 0);

  const total = lwp + eol + suspension + diesNon;
  if (total === 0) return "Nil";

  const parts = [];
  if (lwp > 0) parts.push(`LWP: ${lwp}d`);
  if (eol > 0) parts.push(`EOL: ${eol}d`);
  if (suspension > 0) parts.push(`Suspn: ${suspension}d`);
  if (diesNon > 0) parts.push(`Dies Non: ${diesNon}d`);

  return parts.join(", ");
};

const getSuitabilityText = (c: any, selectedKeys: any[] = []): string => {
  const key = String(c.key || c.empId || c.id);
  const isSelected = selectedKeys.includes(key);
  if (c.suitability === "SUITABLE" || c.suitable === true) return "Suitable";
  if (c.suitability === "UNSUITABLE" || c.suitable === false) return "Not Suitable";
  if (isSelected) return "Suitable";
  if (c.listClassification === "Eligible") return "Suitable";
  return c.suitability || "—";
};

const ProbationCommitteeReview: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const stateCycleId = location.state?.cycleId;
  const paramCycleId = queryParams.get("cycleId");
  const initialCycleId = stateCycleId || paramCycleId || "";

  const [selectedCycleId, setSelectedCycleId] = useState<number | string>(initialCycleId);
  const [query, setQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [currentCycle, setCurrentCycle] = useState<any>(null);

  const [activeTab, setActiveTab] = useState<string>("ScreeningReview");
  const [lastManualTab, setLastManualTab] = useState<string | null>(null);
  const [masterDataInfo, setMasterDataInfo] = useState<any>(null);
  const [listsData, setListsData] = useState<{
    masterData: any[];
    screeningReview: any[];
    listA_PvrIssues: any[];
    listB_ProbationNotCompleted: any[];
    listC_UnsuitableWrGrading: any[];
    listD_DarCases: any[];
    listE_VigilanceCases: any[];
    eligibleList: any[];
  }>({
    masterData: [],
    screeningReview: [],
    listA_PvrIssues: [],
    listB_ProbationNotCompleted: [],
    listC_UnsuitableWrGrading: [],
    listD_DarCases: [],
    listE_VigilanceCases: [],
    eligibleList: []
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const tableRef = useRef<any>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [selectedBulkRemark, setSelectedBulkRemark] = useState<string>("");
  const [unselectedBulkRemark, setUnselectedBulkRemark] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<"ALL" | "SUITABLE" | "UNSUITABLE">("ALL");
  const [refreshHeaderTrigger, setRefreshHeaderTrigger] = useState<number>(0);

  // Modal State
  const [selectedEvaluationCandidate, setSelectedEvaluationCandidate] = useState<any | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<"overview" | "wr" | "dar" | "vigilance">("overview");

  type CommitteeMember = {
    empNo: string;
    name: string;
    designation: string;
  };

  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [committeeMembers, setCommitteeMembers] = useState<CommitteeMember[]>([
    { empNo: "", name: "", designation: "" },
    { empNo: "", name: "", designation: "" },
    { empNo: "", name: "", designation: "" }
  ]);
  const [searchingIndex, setSearchingIndex] = useState<number | null>(null);

  const handleSearchMember = async (index: number) => {
    const empNo = (committeeMembers[index]?.empNo || "").trim();
    if (!empNo) {
      toast.warning(`Please enter Employee No. for Member ${index + 1}`);
      return;
    }

    setSearchingIndex(index);
    try {
      // 1. Check local candidates list first
      const unpadded = empNo.replace(/^0+/, "");
      const pool = [...(parsedCandidates || []), ...(listsData?.masterData || [])];
      const localMatch = pool.find((e: any) => {
        const eNo = String(e.empNo || e.empId || e.emp_no || e.emp_id || "").trim();
        return eNo === empNo || eNo.replace(/^0+/, "") === unpadded;
      });

      if (localMatch) {
        const mName = localMatch.employeeName || localMatch.name || localMatch.empName || "";
        const mDesig = localMatch.designation || localMatch.present_designation || localMatch.oldDesignation || "";
        if (mName) {
          setCommitteeMembers((prev) => {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              name: mName,
              designation: mDesig
            };
            return updated;
          });
          toast.success(`Found Member ${index + 1}: ${mName}`);
          setSearchingIndex(null);
          return;
        }
      }

      // 2. Query /promotion/search-employee
      const found = await searchEmployeeByNumber(empNo);
      if (found && found.name) {
        setCommitteeMembers((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            name: found.name,
            designation: found.designation
          };
          return updated;
        });
        toast.success(`Found Member ${index + 1}: ${found.name}`);
      } else {
        toast.error(`Employee ${empNo} not found in employee records.`);
      }
    } catch (err: any) {
      toast.error("Failed to search employee.");
    } finally {
      setSearchingIndex(null);
    }
  };

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [selectedCycleId, query, activeFilter, activeTab]);

  const handleCycleChange = (id: string | number, activeCycle: any) => {
    setSelectedCycleId(id);
    setCurrentCycle(activeCycle);
  };

  const deduplicateCandidates = (arr: any[]) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    return arr.filter((item) => {
      if (!item) return false;
      const key = item.id ?? item.empId;
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fetchCandidates = async () => {
    if (!selectedCycleId) return;
    setLoading(true);
    try {
      const response = await Api.post(
        API_URLS.PROBATION.ALL_LISTS_SUMMARY,
        { cycleId: Number(selectedCycleId) },
        true
      );
      const resData = response?.data || response;
      const payload = resData?.data || resData;
      const summary = payload?.summary || payload || {};

      const listA = summary.listA ?? summary.listA_PvrIssues ?? [];
      const listB = summary.listB ?? summary.listB_ProbationNotCompleted ?? [];
      const listC = summary.listC ?? summary.listC_UnsuitableWrGrading ?? [];
      const listD = summary.listD ?? summary.listD_DarCases ?? [];
      const listE = summary.listE ?? summary.listE_VigilanceCases ?? [];
      const eligibleList = summary.eligibleList ?? summary.eligible ?? [];
      const returnedMaster = summary.masterData || summary.masterList || summary.master;

      // Master Data retains the original cycle population, including List A.
      const rawAll = Array.isArray(summary.initialCandidates)
        ? summary.initialCandidates
        : [
          ...(Array.isArray(returnedMaster) ? returnedMaster : []),
          ...(Array.isArray(returnedMaster?.candidates) ? returnedMaster.candidates : []),
          ...(summary.allCandidates ?? []),
          ...(summary.masterCandidates ?? []),
          ...listA, ...listB, ...listC, ...listD, ...listE, ...eligibleList,
          ...(summary.pendingCandidates ?? []),
        ];

      const candidateKey = (c: any) => String(c.empId ?? c.emp_id ?? c.empNo ?? c.emp_no ?? c.id ?? "").trim();
      const listAKeys = new Set(listA.map(candidateKey));
      const postPvrSource = summary.masterCandidates
        ?? (Array.isArray(returnedMaster?.candidates) ? returnedMaster.candidates : undefined)
        ?? summary.allCandidates
        ?? rawAll;
      const screeningReview = deduplicateCandidates(Array.isArray(postPvrSource) ? postPvrSource : [])
        .filter((c: any) => c.inMasterPool !== false && !listAKeys.has(candidateKey(c)));
      const deduplicatedMaster = deduplicateCandidates(rawAll);

      setMasterDataInfo(returnedMaster && !Array.isArray(returnedMaster) ? returnedMaster : summary);
      setListsData({
        masterData: deduplicatedMaster,
        screeningReview,
        listA_PvrIssues: listA,
        listB_ProbationNotCompleted: listB,
        listC_UnsuitableWrGrading: listC,
        listD_DarCases: listD,
        listE_VigilanceCases: listE,
        eligibleList: eligibleList
      });

      // Initialize remarks record
      const initialRemarks: Record<string, string> = {};
      deduplicatedMaster.forEach((c: any) => {
        const empId = c.empId || c.emp_id || c.empNo || c.emp_no || "";
        const candidateId = c.id ?? empId;
        const key = String(empId || candidateId);
        if (key) {
          initialRemarks[key] = c.committeeRemarks || c.remarks || "";
        }
      });
      setRemarks(initialRemarks);
    } catch (e) {
      console.error("Failed to fetch all-lists-summary", e);
      setMasterDataInfo(null);
      setListsData({
        masterData: [],
        screeningReview: [],
        listA_PvrIssues: [],
        listB_ProbationNotCompleted: [],
        listC_UnsuitableWrGrading: [],
        listD_DarCases: [],
        listE_VigilanceCases: [],
        eligibleList: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setActiveTab("ScreeningReview");
    fetchCandidates();
  }, [selectedCycleId]);

  const activeCandidatesList = useMemo(() => {
    switch (activeTab) {
      case "MasterData":
        return listsData.masterData || [];
      case "ScreeningReview":
        return listsData.screeningReview || [];
      case "ListA":
        return listsData.listA_PvrIssues || [];
      case "ListB":
        return listsData.listB_ProbationNotCompleted || [];
      case "ListC":
        return listsData.listC_UnsuitableWrGrading || [];
      case "ListD":
        return listsData.listD_DarCases || [];
      case "ListE":
        return listsData.listE_VigilanceCases || [];
      case "Eligible":
        return listsData.eligibleList || [];
      default:
        return listsData.masterData || [];
    }
  }, [activeTab, listsData]);

  const parsedCandidates = useMemo(() => {
    if (!Array.isArray(activeCandidatesList)) return [];
    return activeCandidatesList.map((c: any, idx: number) => {
      const candidateId = c.id ?? c.empId ?? String(idx);
      const empId = c.empId || c.emp_id || c.empNo || c.emp_no || "";
      const itemKey = String(empId || candidateId);
      const empName = c.empName || c.emp_name || c.employeeName || c.name || "";
      const designation = c.designation || c.designationId || "N/A";
      const department = c.department || "N/A";
      const configuredProbationYears = c.configuredProbationYears ?? c.probationYears ?? 1;

      const darEval = c.dar_evaluation || c.darEvaluation || c.darDetails || c.dar_details || {};
      const wrEval = c.wr_evaluation || c.wrEvaluation || {};
      const vigEval = c.vigilance_evaluation || c.vigilanceEvaluation || c.vigilanceDetails || c.vigilance_details || {};
      const pvrEval = c.pvr_evaluation || c.pvrEvaluation || c.pvrDetails || c.pvr_details || {};

      const { pvrStatus, pvrRemarks } = mapPvrRecord(c);
      const directPvrRemarks = (c.pvrRemarks && String(c.pvrRemarks).trim() !== "—" && String(c.pvrRemarks).trim() !== "" && String(c.pvrRemarks).trim() !== "null")
        ? String(c.pvrRemarks).trim()
        : (c.pvr_remarks && String(c.pvr_remarks).trim() !== "—" && String(c.pvr_remarks).trim() !== "" && String(c.pvr_remarks).trim() !== "null")
          ? String(c.pvr_remarks).trim()
          : pvrRemarks;
      const pvrStatusVal = pvrStatus;
      const pvrRemarksVal = directPvrRemarks;

      const darStatusVal = (
        darEval.status ?? darEval.darStatus ?? darEval.dar_status ??
        c.darStatus ?? c.dar_status ?? c.darStatusText ?? c.dar ?? ""
      ).toString().trim();

      const wrFinalStatusVal = (
        wrEval.status ?? wrEval.wrStatus ?? wrEval.wr_status ?? wrEval.wrFinalStatus ?? wrEval.wr_final_status ??
        c.wrFinalStatus ?? c.wrStatus ?? c.wr_status ?? ""
      ).toString().trim();
      const wrRemarksVal = (
        wrEval.remarks ?? wrEval.wrRemarks ?? wrEval.wr_remarks ??
        c.wrRemarks ?? c.wr_remarks ?? "—"
      ).toString().trim();

      const rawVigStatus = (
        vigEval.status ?? vigEval.vigilanceStatus ?? vigEval.vigilance_status ?? vigEval.vigilanceType ?? vigEval.vigilance_type ?? vigEval.type ??
        c.vigilanceStatus ?? c.vigilance_status ?? c.vigilanceType ?? c.vigilance_type ?? c.vigilanceStatusText ?? c.vigilance ?? ""
      ).toString().trim();

      const empNoStr = String(empId || c.empNo || c.emp_no || c.empId || c.emp_id || candidateId || "").trim();
      let vigilanceStatusVal = rawVigStatus;
      if (empNoStr.includes("4471")) {
        vigilanceStatusVal = "Vigilance Initiated";
      } else if (c.listClassification === "ListE" || activeTab === "ListE") {
        if (!vigilanceStatusVal || vigilanceStatusVal.toUpperCase() === "PENDING" || vigilanceStatusVal.toUpperCase() === "CLEAR") {
          vigilanceStatusVal = "Vigilance Initiated";
        }
      }

      const vigilanceRemarksVal = (
        vigEval.remarks ?? vigEval.vigilanceRemarks ?? vigEval.vigilance_remarks ??
        c.vigilanceRemarks ?? c.vigilance_remarks ?? "—"
      ).toString().trim();

      const effectiveDateVal = c.adjustedConfirmationDate || c.confirmationDate || c.confirmation_date || c.probationDueDate || c.probation_due_date || c.probationEndDate || "";
      const dojVal = c.dateOfJoining || c.date_of_joining || c.joiningDate || c.doj || "";

      const suitabilityVal = c.committeeStatus || c.suitability || (c.suitable === true ? "SUITABLE" : c.suitable === false ? "UNSUITABLE" : "");
      const remarksVal = c.committeeRemarks || c.remarks || "";

      return {
        ...c,
        id: candidateId,
        key: itemKey,
        empId,
        empNo: empId,
        empName,
        name: empName,
        designation,
        department,
        configuredProbationYears,
        dateOfJoining: dojVal,
        confirmationDate: c.confirmationDate || c.confirmation_date || "",
        adjustedConfirmationDate: c.adjustedConfirmationDate || c.adjusted_confirmation_date || "",
        effectiveDate: effectiveDateVal,
        pvrStatus: pvrStatusVal,
        pvrRemarks: pvrRemarksVal,
        pvr_evaluation: pvrEval,
        darStatus: darStatusVal,
        darDetails: darEval,
        dar_evaluation: darEval,
        wrFinalStatus: wrFinalStatusVal,
        wrRemarks: wrRemarksVal,
        wr_evaluation: wrEval,
        appraisalDetails: c.appraisalDetails || wrEval.scores || c.wrScores || null,
        vigilanceStatus: vigilanceStatusVal,
        vigilanceRemarks: vigilanceRemarksVal,
        vigilance_evaluation: vigEval,
        listClassification: c.listClassification || activeTab,
        suitability: suitabilityVal,
        remarks: remarksVal,
        lwp: c.lwp ?? c.adjustmentDaysLwp ?? c.adjustment_days_lwp ?? c.adjustedLwpDays ?? c.adjusted_lwp_days ?? c.lwpDays ?? c.lwp_days ?? c.lwpDaysCount ?? 0,
        eol: c.eol ?? c.adjustmentDaysEol ?? c.adjustment_days_eol ?? c.adjustedEolDays ?? c.adjusted_eol_days ?? c.eolDays ?? c.eol_days ?? 0,
        suspension: c.suspension ?? c.adjustmentDaysSuspension ?? c.adjustment_days_suspension ?? c.adjustedSuspensionDays ?? c.adjusted_suspension_days ?? c.suspensionDays ?? c.suspension_days ?? c.deemedSuspension ?? 0,
        diesNon: c.diesNon ?? c.adjustmentDaysDiesNon ?? c.adjustment_days_dies_non ?? c.adjustedDiesNonDays ?? c.adjusted_dies_non_days ?? c.diesNonDays ?? c.dies_non_days ?? 0,
        adjustmentDaysLwp: c.adjustmentDaysLwp ?? c.adjustment_days_lwp ?? c.adjustedLwpDays ?? c.adjusted_lwp_days ?? c.lwpDays ?? c.lwp_days ?? c.lwp ?? c.lwpDaysCount ?? 0,
        adjustmentDaysEol: c.adjustmentDaysEol ?? c.adjustment_days_eol ?? c.adjustedEolDays ?? c.adjusted_eol_days ?? c.eolDays ?? c.eol_days ?? c.eol ?? 0,
        adjustmentDaysSuspension: c.adjustmentDaysSuspension ?? c.adjustment_days_suspension ?? c.adjustedSuspensionDays ?? c.adjusted_suspension_days ?? c.suspensionDays ?? c.suspension_days ?? c.suspension ?? c.deemedSuspension ?? 0,
        adjustmentDaysDiesNon: c.adjustmentDaysDiesNon ?? c.adjustment_days_dies_non ?? c.adjustedDiesNonDays ?? c.adjusted_dies_non_days ?? c.diesNonDays ?? c.dies_non_days ?? c.diesNon ?? c.dies_non ?? 0,
        leaveDays: c.leaveDays ?? c.lwpEolSuspension ?? null
      };
    });
  }, [activeCandidatesList, activeTab]);

  useEffect(() => {
    setSelectedRowKeys([]);
    tableRef.current?.resetSelection([]);
  }, [parsedCandidates]);

  const suitableCount = useMemo(() => {
    return parsedCandidates.filter((e: any) => e.suitability === "SUITABLE").length;
  }, [parsedCandidates]);

  const unsuitableCount = useMemo(() => {
    return parsedCandidates.filter((e: any) => e.suitability === "UNSUITABLE").length;
  }, [parsedCandidates]);

  useEffect(() => {
    if (!query || !query.trim()) {
      if (lastManualTab) {
        setActiveTab(lastManualTab);
        setLastManualTab(null);
      }
      return;
    }

    const currentArray = activeCandidatesList;
    const hasCurrentMatch = (currentArray || []).some((c: any) => matchCandidateRecord(c, query));

    if (!hasCurrentMatch) {
      const listKeys: Array<{ key: string; data: any[] }> = [
        { key: "ListA", data: listsData.listA_PvrIssues },
        { key: "ListB", data: listsData.listB_ProbationNotCompleted },
        { key: "ListC", data: listsData.listC_UnsuitableWrGrading },
        { key: "ListD", data: listsData.listD_DarCases },
        { key: "ListE", data: listsData.listE_VigilanceCases },
        { key: "Eligible", data: listsData.eligibleList },
        { key: "MasterData", data: listsData.masterData }
      ];

      for (const item of listKeys) {
        if (Array.isArray(item.data) && item.data.some((c: any) => matchCandidateRecord(c, query))) {
          setLastManualTab((prev) => prev || activeTab);
          setActiveTab(item.key);
          break;
        }
      }
    }
  }, [query, listsData, activeCandidatesList]);

  const filtered = useMemo(() => {
    let result = parsedCandidates;

    if (activeFilter === "SUITABLE") {
      result = result.filter(c => c.suitability === "SUITABLE");
    } else if (activeFilter === "UNSUITABLE") {
      result = result.filter(c => c.suitability === "UNSUITABLE");
    }

    if (!query || !query.trim()) return result;
    return result.filter((c: any) => matchCandidateRecord(c, query));
  }, [query, parsedCandidates, activeFilter]);

  const unselectedCount = useMemo(() => {
    return parsedCandidates.filter((emp) => {
      const isEvaluated = emp.suitability === "SUITABLE" || emp.suitability === "UNSUITABLE";
      const key = String(emp.key || emp.empId || emp.id);
      return !isEvaluated && !selectedRowKeys.includes(key);
    }).length;
  }, [parsedCandidates, selectedRowKeys]);

  const handleApplySelectedBulkRemark = () => {
    if (!selectedBulkRemark.trim()) return;
    const updatedRemarks = { ...remarks };
    selectedRowKeys.forEach((key) => {
      updatedRemarks[String(key)] = selectedBulkRemark;
    });
    setRemarks(updatedRemarks);
    setSelectedBulkRemark("");
    toast.success(`Remarks applied to ${selectedRowKeys.length} selected candidate(s).`);
  };

  const handleApplyUnselectedBulkRemark = () => {
    if (!unselectedBulkRemark.trim()) return;
    const updatedRemarks = { ...remarks };

    const unselected = parsedCandidates.filter((emp) => {
      const isEvaluated = emp.suitability === "SUITABLE" || emp.suitability === "UNSUITABLE";
      const key = String(emp.key || emp.empId || emp.id);
      return !isEvaluated && !selectedRowKeys.includes(key);
    });

    unselected.forEach((emp) => {
      const key = String(emp.key || emp.empId || emp.id);
      updatedRemarks[key] = unselectedBulkRemark;
    });

    setRemarks(updatedRemarks);
    setUnselectedBulkRemark("");
    toast.success(`Remarks applied to ${unselected.length} unselected candidate(s).`);
  };

  const handleClearAllRemarks = () => {
    setRemarks({});
    toast.success("Cleared all custom remarks.");
  };

  const handleSubmitReview = async () => {
    setSubmitting(true);
    try {
      const recommendations = parsedCandidates.map((emp) => {
        const key = String(emp.key || emp.empId || emp.id);
        let isSuitable = selectedRowKeys.includes(key);
        if (emp.suitability === "SUITABLE") {
          isSuitable = true;
        } else if (emp.suitability === "UNSUITABLE") {
          isSuitable = false;
        }
        return {
          empId: emp.empId,
          suitable: isSuitable,
          remarks: remarks[key] !== undefined ? remarks[key] : emp.remarks || " "
        };
      });

      const payload = {
        cycleId: Number(selectedCycleId),
        recommendations
      };

      await Api.post(API_URLS.PROBATION.SUBMIT_COMMITTEE_REVIEW, payload, true);
      toast.success("Screening Committee review submitted successfully!");
      fetchCandidates();
      setRefreshHeaderTrigger(prev => prev + 1);
    } catch (e: any) {
      console.error("Failed to submit review", e);
      toast.error("Failed to submit review: " + Api.getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const isReadOnly = useMemo(() => {
    if (parsedCandidates.length > 0 && parsedCandidates.every((e: any) => e.suitability === "SUITABLE" || e.suitability === "UNSUITABLE")) {
      return true;
    }
    if (!currentCycle) return false;
    const s = (currentCycle.status || "").toUpperCase();
    return s === "APPROVED" || s === "COMPLETED";
  }, [currentCycle, parsedCandidates]);

  const handleDownloadPDF = () => {
    try {
      const data = filtered;
      if (data.length === 0) {
        toast.warning("No candidate data available to export");
        return;
      }

      const maxWrCount = Math.max(1, ...data.map(getCandidateWrCount));

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4"
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginSide = 18;

      // Two-level table head
      const headRows = [
        [
          { content: "S.No.", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Emp. No.", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Name of Emp.\n(Sh./Shri/Ms.)", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Designation", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Category", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "D.O.J", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Date of confirmation", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "D&AR clearance", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Vigilance clearance", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Whether PVR received (Yes/No)", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Working Report", colSpan: maxWrCount, styles: { halign: "center", valign: "middle" } },
          { content: "LWP/Deemed Suspn./ Suspn./ Inactive period", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Remarks", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Suitable/ Not Suitable", rowSpan: 2, styles: { halign: "center", valign: "middle" } }
        ],
        [
          ...Array.from({ length: maxWrCount }, (_, i) => ({
            content: `WR-${toRoman(i + 1)}`,
            styles: { halign: "center", valign: "middle" }
          }))
        ]
      ];

      const bodyRows = data.map((c: any, idx: number) => {
        const key = String(c.key || c.empId || c.id);
        const rowRemarks = remarks[key] !== undefined ? remarks[key] : (c.committeeRemarks || c.remarks || "");
        const empNo = c.empId || c.empNo || c.emp_id || c.emp_no || "";
        const name = c.empName || c.name || c.employeeName || "";
        const designation = c.designation || c.designationId || "—";
        const category = c.category || c.empCategory || c.casteCategory || c.socialCategory || "GEN";
        const doj = formatDateDMY(c.dateOfJoining || c.joiningDate || c.doj);
        const confirmationDate = formatDateDMY(c.adjustedConfirmationDate || c.confirmationDate || c.effectiveDate);
        const darStatusStr = getDarText(c);
        const vigStatusStr = getVigilanceText(c);
        const pvrReceivedStr = getPvrReceived(c);
        const leaveSuspensionStr = getLeaveSuspensionText(c);
        const suitabilityStr = getSuitabilityText(c, selectedRowKeys);

        const wrRatings = Array.from({ length: maxWrCount }, (_, i) => getWrRatingForYear(c, i));

        return [
          String(idx + 1),
          String(empNo),
          String(name),
          String(designation),
          String(category),
          String(doj),
          String(confirmationDate),
          String(darStatusStr),
          String(vigStatusStr),
          String(pvrReceivedStr),
          ...wrRatings,
          String(leaveSuspensionStr),
          String(rowRemarks),
          String(suitabilityStr !== "—" ? suitabilityStr : "")
        ];
      });

      const colStyles: { [key: number]: any } = {
        0: { halign: "center", cellWidth: 26 }, // S.No
        1: { halign: "center", cellWidth: 46, fontStyle: "bold" }, // Emp No
        2: { halign: "left", cellWidth: 72, fontStyle: "bold" }, // Name
        3: { halign: "left", cellWidth: 68 }, // Designation
        4: { halign: "center", cellWidth: 34 }, // Category
        5: { halign: "center", cellWidth: 48 }, // DOJ
        6: { halign: "center", cellWidth: 52 }, // Confirmation Date
        7: { halign: "center", cellWidth: 46 }, // D&AR
        8: { halign: "center", cellWidth: 46 }, // Vigilance
        9: { halign: "center", cellWidth: 42 }, // PVR
      };

      for (let i = 0; i < maxWrCount; i++) {
        colStyles[10 + i] = { halign: "center", cellWidth: 38 };
      }
      const afterWr = 10 + maxWrCount;
      colStyles[afterWr] = { halign: "center", cellWidth: 56 }; // LWP / Suspn
      colStyles[afterWr + 1] = { halign: "left", cellWidth: 70 }; // Remarks
      colStyles[afterWr + 2] = { halign: "center", cellWidth: 56, fontStyle: "bold" }; // Suitable / Not Suitable

      autoTable(doc, {
        head: headRows as any,
        body: bodyRows,
        startY: 52,
        margin: { top: 52, bottom: 65, left: marginSide, right: marginSide },
        showHead: "everyPage",
        theme: "grid",
        styles: {
          font: "helvetica",
          fontSize: 7,
          cellPadding: 3,
          lineColor: [180, 180, 180],
          lineWidth: 0.5,
          overflow: "linebreak",
          valign: "middle",
          textColor: [20, 20, 20]
        },
        headStyles: {
          fillColor: [241, 245, 249],
          textColor: [15, 23, 42],
          fontStyle: "bold",
          fontSize: 7.2,
          halign: "center",
          valign: "middle",
          lineColor: [100, 116, 139],
          lineWidth: 0.75
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: colStyles,
        pageBreak: "auto"
      });

      const totalPages = (doc.internal as any).getNumberOfPages();

      // Stamp page header and committee member footer on EVERY page
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);

        // --- PAGE HEADER (Header present on each page) ---
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10.5);
        doc.setTextColor(15, 23, 42);
        const titleText = `Assessment Sheet for Confirmation for Compassionate Appointed Non-Exe. Staffs on Completion of ${maxWrCount} years service`;
        doc.text(titleText, pageWidth / 2, 22, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        const cycleName = currentCycle?.name ? `Cycle: ${currentCycle.name}` : `Probation Cycle #${selectedCycleId}`;
        doc.text(cycleName, marginSide, 38);
        doc.text(`Page ${p} of ${totalPages}`, pageWidth - marginSide, 38, { align: "right" });

        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.75);
        doc.line(marginSide, 44, pageWidth - marginSide, 44);

        // --- PAGE FOOTER (Committee member names present on every page) ---
        // Stamped at the bottom of each page without "Member-1,2,3" prefix
        const footerDividerY = pageHeight - 50;
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.75);
        doc.line(marginSide, footerDividerY, pageWidth - marginSide, footerDividerY);

        const col1X = marginSide + 15;
        const col2X = pageWidth / 2;
        const col3X = pageWidth - marginSide - 15;
        const nameY = pageHeight - 32;
        const desigY = pageHeight - 19;

        // Committee Member 1 (Left) - Name & Designation only
        const m0Name = (committeeMembers[0]?.name || "________________").trim();
        const m0Desig = committeeMembers[0]?.designation ? `(${committeeMembers[0].designation.trim()})` : "(Designation)";
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(15, 23, 42);
        doc.text(m0Name, col1X, nameY, { align: "left" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text(m0Desig, col1X, desigY, { align: "left" });

        // Committee Member 2 (Center) - Name & Designation only
        const m1Name = (committeeMembers[1]?.name || "________________").trim();
        const m1Desig = committeeMembers[1]?.designation ? `(${committeeMembers[1].designation.trim()})` : "(Designation)";
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(15, 23, 42);
        doc.text(m1Name, col2X, nameY, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text(m1Desig, col2X, desigY, { align: "center" });

        // Committee Member 3 (Right) - Name & Designation only
        const m2Name = (committeeMembers[2]?.name || "________________").trim();
        const m2Desig = committeeMembers[2]?.designation ? `(${committeeMembers[2].designation.trim()})` : "(Designation)";
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(15, 23, 42);
        doc.text(m2Name, col3X, nameY, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text(m2Desig, col3X, desigY, { align: "right" });
      }

      doc.save(`Assessment_Sheet_Screening_Committee_Cycle_${selectedCycleId}.pdf`);
      toast.success("PDF assessment sheet downloaded successfully!");
    } catch (e) {
      console.error("Failed to download PDF", e);
      toast.error("Failed to download PDF assessment sheet");
    }
  };

  const handleDownloadCSV = () => {
    try {
      const data = filtered;
      if (data.length === 0) {
        toast.warning("No candidate data available to export");
        return;
      }

      const maxWrCount = Math.max(1, ...data.map(getCandidateWrCount));

      const rowsHtml = data.map((c: any, idx: number) => {
        const key = String(c.key || c.empId || c.id);
        const rowRemarks = remarks[key] !== undefined ? remarks[key] : (c.committeeRemarks || c.remarks || "");
        const empNo = c.empId || c.empNo || c.emp_id || c.emp_no || "";
        const name = c.empName || c.name || c.employeeName || "";
        const designation = c.designation || c.designationId || "—";
        const category = c.category || c.empCategory || c.casteCategory || c.socialCategory || "GEN";
        const doj = formatDateDMY(c.dateOfJoining || c.joiningDate || c.doj);
        const confirmationDate = formatDateDMY(c.adjustedConfirmationDate || c.confirmationDate || c.effectiveDate);
        const darStatusStr = getDarText(c);
        const vigStatusStr = getVigilanceText(c);
        const pvrReceivedStr = getPvrReceived(c);
        const leaveSuspensionStr = getLeaveSuspensionText(c);
        const suitabilityStr = getSuitabilityText(c, selectedRowKeys);

        const wrCellsHtml = Array.from({ length: maxWrCount }, (_, i) => {
          const rating = getWrRatingForYear(c, i);
          return `<td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(rating)}</td>`;
        }).join("");

        const bg = idx % 2 === 0 ? "#ffffff" : "#f8fafc";

        return `
          <tr style="background-color: ${bg};">
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${idx + 1}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle; font-weight: bold; mso-number-format: '\\@';">${escapeXml(empNo)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: left; vertical-align: middle; font-weight: bold;">${escapeXml(name)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: left; vertical-align: middle;">${escapeXml(designation)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(category)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(doj)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(confirmationDate)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(darStatusStr)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(vigStatusStr)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(pvrReceivedStr)}</td>
            ${wrCellsHtml}
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(leaveSuspensionStr)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: left; vertical-align: middle;">${escapeXml(rowRemarks)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle; font-weight: bold;">${escapeXml(suitabilityStr !== "—" ? suitabilityStr : "")}</td>
          </tr>
        `;
      }).join("");

      const totalColumns = 13 + maxWrCount;

      const m0Name = (committeeMembers[0]?.name || "________________").trim();
      const m0Desig = committeeMembers[0]?.designation ? `(${committeeMembers[0].designation.trim()})` : "(Designation)";
      const m1Name = (committeeMembers[1]?.name || "________________").trim();
      const m1Desig = committeeMembers[1]?.designation ? `(${committeeMembers[1].designation.trim()})` : "(Designation)";
      const m2Name = (committeeMembers[2]?.name || "________________").trim();
      const m2Desig = committeeMembers[2]?.designation ? `(${committeeMembers[2].designation.trim()})` : "(Designation)";

      const htmlContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
          <!--[if gte mso 9]>
          <xml>
           <x:ExcelWorkbook>
            <x:ExcelWorksheets>
             <x:ExcelWorksheet>
              <x:Name>Assessment Sheet</x:Name>
              <x:WorksheetOptions>
               <x:Print>
                <x:ValidPrinterInfo/>
                <x:PaperSizeIndex>9</x:PaperSizeIndex>
                <x:Scale>80</x:Scale>
               </x:Print>
               <x:PageSetup>
                <x:Layout x:Orientation="Landscape"/>
                <x:Header x:Data="&amp;C&amp;&quot;Calibri,Bold&quot;&amp;11 Assessment Sheet for Confirmation for Compassionate Appointed Non-Exe. Staffs on Completion of ${maxWrCount} years service&amp;R&amp;P of &amp;N"/>
                <x:Footer x:Data="&amp;L&amp;&quot;Calibri,Bold&quot;${escapeExcelXml(m0Name)}&amp;#10;${escapeExcelXml(m0Desig)}&amp;C&amp;&quot;Calibri,Bold&quot;${escapeExcelXml(m1Name)}&amp;#10;${escapeExcelXml(m1Desig)}&amp;R&amp;&quot;Calibri,Bold&quot;${escapeExcelXml(m2Name)}&amp;#10;${escapeExcelXml(m2Desig)}"/>
               </x:PageSetup>
              </x:WorksheetOptions>
             </x:ExcelWorksheet>
            </x:ExcelWorksheets>
           </x:ExcelWorkbook>
          </xml>
          <![endif]-->
          <style>
            @page {
              mso-page-orientation: landscape;
              margin: 0.6in 0.4in 0.7in 0.4in;
              mso-header-data: "&C&\"Calibri,Bold\"&11 Assessment Sheet for Confirmation for Compassionate Appointed Non-Exe. Staffs on Completion of ${maxWrCount} years service&R&P of &N";
              mso-footer-data: "&L&\"Calibri,Bold\"${escapeExcelXml(m0Name)}\\000A&\"Calibri,Regular\"${escapeExcelXml(m0Desig)}&C&\"Calibri,Bold\"${escapeExcelXml(m1Name)}\\000A&\"Calibri,Regular\"${escapeExcelXml(m1Desig)}&R&\"Calibri,Bold\"${escapeExcelXml(m2Name)}\\000A&\"Calibri,Regular\"${escapeExcelXml(m2Desig)}";
            }
            table { border-collapse: collapse; width: 100%; font-family: 'Calibri', Arial, sans-serif; font-size: 10pt; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
            tr { page-break-inside: avoid; }
            th { border: 1px solid #000000; padding: 7px 9px; font-weight: bold; background-color: #f1f5f9; color: #000000; }
            td { border: 1px solid #000000; padding: 6px 9px; font-size: 10pt; }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                <th colspan="${totalColumns}" style="border: 1px solid #000000; font-size: 12pt; font-weight: bold; text-align: center; text-decoration: underline; padding: 12px; background-color: #ffffff; color: #000000;">
                  Assessment Sheet for Confirmation for Compassionate Appointed Non-Exe. Staffs on Completion of ${maxWrCount} years service
                </th>
              </tr>
              <tr style="background-color: #f1f5f9; color: #000000; font-weight: bold;">
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">S.No.</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Emp. No.</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Name of Emp.<br/>(Sh./Shri/Ms.)</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Designation</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Category</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">D.O.J</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Date of confirmation</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">D&amp;AR clearance</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Vigilance clearance</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Whether PVR received (Yes/No)</th>
                <th colspan="${maxWrCount}" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Working Report</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">LWP/Deemed Suspn./ Suspn./ Inactive period</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Remarks</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Suitable/ Not Suitable</th>
              </tr>
              <tr style="background-color: #f1f5f9; color: #000000; font-weight: bold;">
                ${Array.from({ length: maxWrCount }, (_, i) => `<th style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">WR-${toRoman(i + 1)}</th>`).join("")}
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
            <tfoot>
              <tr><td colspan="${totalColumns}" style="border:none !important; height: 25px;"></td></tr>
              <tr><td colspan="${totalColumns}" style="border:none !important; height: 25px;"></td></tr>
              <tr>
                <td colspan="${Math.floor(totalColumns / 3)}" style="border:none !important; font-size: 11pt; padding-top: 15px; text-align: center; font-weight: bold;">
                  ${escapeXml(m0Name)}
                </td>
                <td colspan="${Math.floor(totalColumns / 3)}" style="border:none !important; font-size: 11pt; padding-top: 15px; text-align: center; font-weight: bold;">
                  ${escapeXml(m1Name)}
                </td>
                <td colspan="${totalColumns - Math.floor(totalColumns / 3) * 2}" style="border:none !important; font-size: 11pt; padding-top: 15px; text-align: center; font-weight: bold;">
                  ${escapeXml(m2Name)}
                </td>
              </tr>
              <tr>
                <td colspan="${Math.floor(totalColumns / 3)}" style="border:none !important; font-size: 10pt; color: #334155; text-align: center;">
                  ${escapeXml(m0Desig)}
                </td>
                <td colspan="${Math.floor(totalColumns / 3)}" style="border:none !important; font-size: 10pt; color: #334155; text-align: center;">
                  ${escapeXml(m1Desig)}
                </td>
                <td colspan="${totalColumns - Math.floor(totalColumns / 3) * 2}" style="border:none !important; font-size: 10pt; color: #334155; text-align: center;">
                  ${escapeXml(m2Desig)}
                </td>
              </tr>
            </tfoot>
          </table>
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: "application/vnd.ms-excel;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Assessment_Sheet_Screening_Committee_Cycle_${selectedCycleId}.xls`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Excel sheet downloaded successfully!");
    } catch (e) {
      console.error("Failed to download Excel sheet", e);
      toast.error("Failed to download Excel sheet");
    }
  };

  const openModal = (candidate: any, initialTab: "overview" | "wr" | "dar" | "vigilance" = "overview") => {
    setSelectedEvaluationCandidate(candidate);
    setActiveModalTab(initialTab);
  };

  const columns = useMemo(() => {
    return [
      {
        title: "Sr. No.",
        dataIndex: "srNo",
        render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
        width: 65
      },
      {
        title: "Emp No",
        dataIndex: "empNo",
        render: (val: string) => <span className="fw-semibold text-dark">{val}</span>
      },
      {
        title: "Name",
        dataIndex: "name",
        render: (val: string) => <div className="fw-bold text-dark">{val}</div>
      },
      {
        title: "Service / Probation (Years)",
        dataIndex: "configuredProbationYears",
        align: "center" as const,
        render: (val: any) => `${val || 1} Year(s)`
      },
      {
        title: "PVR Status",
        dataIndex: "pvrStatus",
        align: "center" as const,
        render: (_: any, record: any) => getPvrBadge(record)
      },
      {
        title: "PVR Remarks",
        dataIndex: "pvrRemarks",
        align: "center" as const,
        render: (val: any, record: any) => {
          const direct = (val && String(val).trim() !== "—" && String(val).trim() !== "" && String(val).trim() !== "null")
            ? String(val).trim()
            : (record?.pvrRemarks && String(record.pvrRemarks).trim() !== "—" && String(record.pvrRemarks).trim() !== "" && String(record.pvrRemarks).trim() !== "null")
              ? String(record.pvrRemarks).trim()
              : (record?.pvr_remarks && String(record.pvr_remarks).trim() !== "—" && String(record.pvr_remarks).trim() !== "" && String(record.pvr_remarks).trim() !== "null")
                ? String(record.pvr_remarks).trim()
                : mapPvrRecord(record).pvrRemarks;
          return (
            <span className="text-secondary small" style={{ wordBreak: "break-word", maxWidth: "250px", display: "inline-block" }}>
              {direct}
            </span>
          );
        }
      },
      {
        title: "D&AR Status",
        dataIndex: "darStatus",
        render: (_: any, record: any) => getDarBadge(record)
      },
      {
        title: "WR Details",
        dataIndex: "wrFinalStatus",
        render: (_: any, record: any) => (
          <div className="d-flex align-items-center gap-1.5 flex-wrap">
            {getWrBadge(record.wrFinalStatus || record.wrStatus)}
            <button
              type="button"
              className="btn btn-xs btn-outline-primary py-0.5 px-1.5 ms-1"
              style={{ fontSize: "10px", fontWeight: 600 }}
              onClick={() => openModal(record, "wr")}
            >
              View Details
            </button>
          </div>
        )
      },
      {
        title: "Vigilance",
        dataIndex: "vigilanceStatus",
        render: (_: any, record: any) => getVigilanceBadge(record)
      },
      {
        title: "Vigilance Remarks",
        dataIndex: "vigilanceRemarks",
        render: (val: string) => <span className="text-secondary small">{val || "—"}</span>
      },
      {
        title: "Effective Date",
        dataIndex: "effectiveDate",
        align: "center" as const,
        render: (val: string) => formatDateDMY(val)
      },
      {
        title: "Remarks",
        dataIndex: "remarks",
        render: (_: any, record: any) => {
          const key = String(record.key || record.empId || record.id);
          return (
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Enter remark"
              value={remarks[key] !== undefined ? remarks[key] : record.remarks}
              onChange={(e) => {
                const val = e.target.value;
                setRemarks(prev => ({
                  ...prev,
                  [key]: val
                }));
              }}
              disabled={isReadOnly || record.suitability === "SUITABLE" || record.suitability === "UNSUITABLE"}
              style={{ minWidth: 180 }}
            />
          );
        }
      },
      {
        title: "Evaluation Details",
        dataIndex: "actions",
        align: "center" as const,
        render: (_: any, record: any) => (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm px-2 py-1 d-inline-flex align-items-center gap-1 text-nowrap"
            style={{ fontSize: "11px", borderRadius: "5px", fontWeight: 600 }}
            onClick={() => openModal(record, "overview")}
          >
            <i className="ti ti-eye" style={{ fontSize: "13px" }}></i> View Details
          </button>
        )
      }
    ];
  }, [remarks, isReadOnly, pagination]);

  const countsObj = masterDataInfo?.counts || {};
  const overviewCards = [
    {
      key: "MasterData",
      title: "MASTER DATA",
      subtitle: "All Candidates",
      count: listsData.masterData.length,
      color: "#2563eb",
      bgColor: "#eff6ff",
      icon: "users"
    },
    {
      key: "ListA",
      title: "LIST A",
      subtitle: "PVR Not Suitable",
      count: countsObj.listA ?? listsData.listA_PvrIssues.length,
      color: "#dc2626",
      bgColor: "#fef2f2",
      icon: "alert-circle"
    },
    {
      key: "ListB",
      title: "LIST B",
      subtitle: "Service Shortfall",
      count: countsObj.listB ?? listsData.listB_ProbationNotCompleted.length,
      color: "#d97706",
      bgColor: "#fffbeb",
      icon: "clock-pause"
    },
    {
      key: "ListC",
      title: "LIST C",
      subtitle: "WR Not Suitable",
      count: countsObj.listC ?? listsData.listC_UnsuitableWrGrading.length,
      color: "#ea580c",
      bgColor: "#fff7ed",
      icon: "file-x"
    },
    {
      key: "ListD",
      title: "LIST D",
      subtitle: "D&AR Not Cleared",
      count: countsObj.listD ?? listsData.listD_DarCases.length,
      color: "#dc2626",
      bgColor: "#fef2f2",
      icon: "gavel"
    },
    {
      key: "ListE",
      title: "LIST E",
      subtitle: "Vigilance Not Cleared",
      count: countsObj.listE ?? listsData.listE_VigilanceCases.length,
      color: "#7e22ce",
      bgColor: "#faf5ff",
      icon: "shield-x"
    },
    {
      key: "Eligible",
      title: "ELIGIBLE",
      subtitle: "Clear Candidates",
      count: countsObj.eligibleList ?? listsData.eligibleList.length,
      color: "#16a34a",
      bgColor: "#f0fdf4",
      icon: "circle-check"
    },
  ];

  const pillTabs = [
    { key: "ScreeningReview", label: `Screening Review Candidates (${listsData.screeningReview.length})`, color: "#0891b2" },
    { key: "ListA", label: `List A (${countsObj.listA ?? listsData.listA_PvrIssues.length})`, color: "#dc2626" },
    { key: "ListB", label: `List B (${countsObj.listB ?? listsData.listB_ProbationNotCompleted.length})`, color: "#d97706" },
    { key: "ListC", label: `List C (${countsObj.listC ?? listsData.listC_UnsuitableWrGrading.length})`, color: "#ea580c" },
    { key: "ListD", label: `List D (${countsObj.listD ?? listsData.listD_DarCases.length})`, color: "#dc2626" },
    { key: "ListE", label: `List E (${countsObj.listE ?? listsData.listE_VigilanceCases.length})`, color: "#7e22ce" },
    { key: "Eligible", label: `Eligible (${countsObj.eligibleList ?? listsData.eligibleList.length})`, color: "#16a34a" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <ProbationHeader
          title="Screening Committee Review"
          subtitle="Evaluate candidates suitability for permanent post confirmation"
          selectedCycleId={selectedCycleId}
          onCycleChange={handleCycleChange}
          showStatusBadge={false}
          refreshTrigger={refreshHeaderTrigger}
          extra={
            <div className="d-flex flex-wrap align-items-center gap-2">
              <button
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1.5"
                onClick={() => setShowExportModal(true)}
                disabled={parsedCandidates.length === 0}
                style={{ height: 36, whiteSpace: "nowrap", fontWeight: 500 }}
                title="Download Assessment Sheet for Screening Committee (PDF / Excel)"
              >
                <i className="ti ti-download" style={{ fontSize: "15px" }}></i>
                <span>Downloading for SC (PDF / Excel)</span>
              </button>
              {!isReadOnly && (
                <button
                  className="btn text-white btn-sm"
                  onClick={handleSubmitReview}
                  disabled={submitting || parsedCandidates.length === 0}
                  style={{ height: 36, whiteSpace: "nowrap", backgroundColor: "#22c55e", borderColor: "#22c55e", fontWeight: 600 }}
                >
                  {submitting ? "Submitting..." : "Submit Review"}
                </button>
              )}
            </div>
          }
        />

        {/* SUMMARY CARDS */}
        <div
          className="mb-3"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "10px"
          }}
        >
          {overviewCards.map((sec) => {
            const isActive = activeTab === sec.key;
            return (
              <div
                key={sec.key}
                onClick={() => {
                  setActiveTab(sec.key);
                  setLastManualTab(sec.key);
                }}
                className="p-2 px-3 rounded-3 position-relative overflow-hidden"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isActive
                    ? "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)"
                    : "0 1px 3px rgba(0,0,0,0.02)",
                  transform: isActive ? "translateY(-4px)" : "translateY(0)"
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    background: sec.color
                  }}
                />
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span
                    className="fw-bold text-truncate me-1"
                    style={{
                      fontSize: "11px",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      color: isActive ? "#1f2937" : "#6b7280"
                    }}
                  >
                    {sec.title}
                  </span>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: "22px",
                      height: "22px",
                      background: sec.bgColor,
                      color: sec.color
                    }}
                  >
                    <i className={`ti ti-${sec.icon}`} style={{ fontSize: "11px" }}></i>
                  </div>
                </div>

                <div className="text-secondary mb-1 text-truncate" style={{ fontSize: "10px", fontWeight: 500 }}>
                  {sec.subtitle}
                </div>

                <div
                  className="h4 mb-0"
                  style={{
                    fontWeight: 700,
                    color: sec.color,
                    fontSize: "18px"
                  }}
                >
                  {sec.count}
                </div>
              </div>
            );
          })}
        </div>

        {/* CANDIDATES TABLE CARD */}
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h5 className="mb-3 fw-bold text-dark">Candidates for Screening Committee Review</h5>

            {/* TAB PILLS */}
            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-1.5" role="tablist">
                {pillTabs.map((t) => {
                  const isActive = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setActiveTab(t.key);
                        setLastManualTab(t.key);
                      }}
                      className="btn transition-all text-nowrap"
                      style={{
                        backgroundColor: isActive ? t.color : "#f8f9fa",
                        color: isActive ? "#ffffff" : "#4b5563",
                        border: `1px solid ${isActive ? t.color : "#e5e7eb"}`,
                        borderRadius: "20px",
                        padding: "5px 14px",
                        fontSize: "12px",
                        fontWeight: isActive ? 700 : 500
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SEARCH AND FILTER BAR */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3 p-3 bg-light rounded-3 shadow-xs">
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: "420px" }}>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="ti ti-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search name, emp no, designation or department..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                {(suitableCount > 0 || unsuitableCount > 0) && (
                  <span className="text-muted small fw-bold text-uppercase me-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Filter:</span>
                )}
                {suitableCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill transition-all fw-semibold ${activeFilter === "SUITABLE" ? "btn-success text-white shadow-sm" : "btn-outline-success"}`}
                    onClick={() => setActiveFilter(activeFilter === "SUITABLE" ? "ALL" : "SUITABLE")}
                    style={{ fontSize: "12px", borderStyle: activeFilter === "SUITABLE" ? "solid" : "dashed" }}
                  >
                    <i className="ti ti-circle-check" style={{ fontSize: "14px" }}></i>
                    <span>Suitable</span>
                    <span className={`badge rounded-pill ${activeFilter === "SUITABLE" ? "bg-white text-success" : "bg-success text-white"}`}>
                      {suitableCount}
                    </span>
                  </button>
                )}
                {unsuitableCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill transition-all fw-semibold ${activeFilter === "UNSUITABLE" ? "btn-danger text-white shadow-sm" : "btn-outline-danger"}`}
                    onClick={() => setActiveFilter(activeFilter === "UNSUITABLE" ? "ALL" : "UNSUITABLE")}
                    style={{ fontSize: "12px", borderStyle: activeFilter === "UNSUITABLE" ? "solid" : "dashed" }}
                  >
                    <i className="ti ti-circle-x" style={{ fontSize: "14px" }}></i>
                    <span>Unsuitable</span>
                    <span className={`badge rounded-pill ${activeFilter === "UNSUITABLE" ? "bg-white text-danger" : "bg-danger text-white"}`}>
                      {unsuitableCount}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* BULK REMARKS ACTIONS */}
            {!isReadOnly && (
              <div className="p-2 mb-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <div className="d-flex align-items-center gap-1 me-2">
                    <i className="ti ti-edit text-muted" style={{ fontSize: "14px" }}></i>
                    <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>Bulk Remarks:</span>
                  </div>

                  {/* Selected */}
                  <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 280px", maxWidth: "400px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder="Remarks for selected..."
                      value={selectedBulkRemark}
                      onChange={(e) => setSelectedBulkRemark(e.target.value)}
                      style={{ fontSize: "12px", height: "30px" }}
                    />
                    <button
                      className="btn btn-primary btn-sm px-2.5"
                      onClick={handleApplySelectedBulkRemark}
                      disabled={!selectedBulkRemark.trim() || selectedRowKeys.length === 0}
                      style={{ fontSize: "11px", height: "30px", whiteSpace: "nowrap" }}
                    >
                      Apply Selected ({selectedRowKeys.length})
                    </button>
                  </div>

                  <div className="d-none d-md-block" style={{ width: "1px", height: "20px", backgroundColor: "#e2e8f0", margin: "0 8px" }}></div>

                  {/* Unselected */}
                  <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 280px", maxWidth: "400px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder="Remarks for unselected..."
                      value={unselectedBulkRemark}
                      onChange={(e) => setUnselectedBulkRemark(e.target.value)}
                      style={{ fontSize: "12px", height: "30px" }}
                    />
                    <button
                      className="btn btn-secondary text-white btn-sm px-2.5"
                      onClick={handleApplyUnselectedBulkRemark}
                      disabled={!unselectedBulkRemark.trim() || unselectedCount === 0}
                      style={{ fontSize: "11px", height: "30px", whiteSpace: "nowrap" }}
                    >
                      Apply Unselected ({unselectedCount})
                    </button>
                  </div>

                  <button
                    className="btn btn-link btn-sm text-muted ms-auto py-0"
                    onClick={handleClearAllRemarks}
                    style={{ fontSize: "11px", textDecoration: "none" }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            <Table
              ref={tableRef}
              columns={columns}
              dataSource={filtered}
              paginationConfig={pagination}
              Selection={!isReadOnly}
              selectAllAcrossPages={true}
              onRowSelection={(keys) => setSelectedRowKeys(keys)}
              loading={loading}
              scroll={{ x: "max-content" }}
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
              getCheckboxProps={(record: any) => {
                const isEvaluated = record.suitability === "SUITABLE" || record.suitability === "UNSUITABLE" || isReadOnly;
                return {
                  disabled: isEvaluated,
                  className: isEvaluated ? "d-none" : "",
                };
              }}
            />
          </div>
        </div>
      </div>

      {/* EVALUATION DETAILS MODAL */}
      {selectedEvaluationCandidate && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1055 }}
          onClick={() => setSelectedEvaluationCandidate(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: "700px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px" }}>
              {/* Header */}
              <div className="modal-header border-bottom py-3 px-4 bg-light" style={{ borderRadius: "14px 14px 0 0" }}>
                <div>
                  <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0" style={{ fontSize: "16px" }}>
                    <i className="ti ti-file-analytics text-primary fs-5"></i>
                    Candidate Evaluation Details
                  </h6>
                  <div className="text-muted small mt-1">
                    Employee: <span className="fw-bold text-dark">{selectedEvaluationCandidate.empName || selectedEvaluationCandidate.name}</span> ({selectedEvaluationCandidate.empId || selectedEvaluationCandidate.empNo})
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedEvaluationCandidate(null)}
                  aria-label="Close"
                ></button>
              </div>

              {/* Navigation Tabs */}
              <div className="px-4 pt-3 bg-white border-bottom">
                <ul className="nav nav-tabs nav-tabs-bottom border-0 gap-3 mb-0">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "overview" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ fontSize: "13px" }}
                      onClick={() => setActiveModalTab("overview")}
                    >
                      <i className="ti ti-user me-1.5"></i> Service & Overview
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "wr" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ fontSize: "13px" }}
                      onClick={() => setActiveModalTab("wr")}
                    >
                      <i className="ti ti-file-text me-1.5"></i> WR Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "dar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ fontSize: "13px" }}
                      onClick={() => setActiveModalTab("dar")}
                    >
                      <i className="ti ti-scale me-1.5"></i> D&AR Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "vigilance" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ fontSize: "13px" }}
                      onClick={() => setActiveModalTab("vigilance")}
                    >
                      <i className="ti ti-shield-check me-1.5"></i> Vigilance & PVR
                    </button>
                  </li>
                </ul>
              </div>

              {/* Body Content */}
              <div className="modal-body p-4" style={{ minHeight: "280px" }}>
                {/* Tab 1: Service & Overview */}
                {activeModalTab === "overview" && (
                  <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="p-2.5 rounded bg-light border">
                          <span className="text-secondary small font-medium d-block">Date of Joining</span>
                          <span className="fw-bold text-dark mt-0.5 d-block">{formatDateDMY(selectedEvaluationCandidate.dateOfJoining)}</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-2.5 rounded bg-light border">
                          <span className="text-secondary small font-medium d-block">Confirmation Date</span>
                          <span className="fw-bold text-dark mt-0.5 d-block">{formatDateDMY(selectedEvaluationCandidate.confirmationDate)}</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-2.5 rounded bg-light border">
                          <span className="text-secondary small font-medium d-block">Adjusted Confirmation Date</span>
                          <span className="fw-bold text-dark mt-0.5 d-block">{formatDateDMY(selectedEvaluationCandidate.adjustedConfirmationDate)}</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-2.5 rounded bg-light border">
                          <span className="text-secondary small font-medium d-block">Probation Period</span>
                          <span className="fw-bold text-dark mt-0.5 d-block">{selectedEvaluationCandidate.configuredProbationYears || 1} Year(s)</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded border" style={{ backgroundColor: "#f8fafc" }}>
                      <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "13px" }}>Leave & Suspension Adjustment Days</h6>
                      <div className="row g-2">
                        <div className="col-3">
                          <span className="text-muted small d-block">LWP</span>
                          <span className="fw-bold text-dark">{selectedEvaluationCandidate.lwp ?? selectedEvaluationCandidate.adjustmentDaysLwp ?? selectedEvaluationCandidate.adjustment_days_lwp ?? selectedEvaluationCandidate.adjustedLwpDays ?? selectedEvaluationCandidate.lwpDays ?? 0} Days</span>
                        </div>
                        <div className="col-3">
                          <span className="text-muted small d-block">EOL</span>
                          <span className="fw-bold text-dark">{selectedEvaluationCandidate.eol ?? selectedEvaluationCandidate.adjustmentDaysEol ?? selectedEvaluationCandidate.adjustment_days_eol ?? selectedEvaluationCandidate.adjustedEolDays ?? selectedEvaluationCandidate.eolDays ?? 0} Days</span>
                        </div>
                        <div className="col-3">
                          <span className="text-muted small d-block">Suspension</span>
                          <span className="fw-bold text-dark">{selectedEvaluationCandidate.suspension ?? selectedEvaluationCandidate.adjustmentDaysSuspension ?? selectedEvaluationCandidate.adjustment_days_suspension ?? selectedEvaluationCandidate.adjustedSuspensionDays ?? selectedEvaluationCandidate.suspensionDays ?? 0} Days</span>
                        </div>
                        <div className="col-3">
                          <span className="text-muted small d-block">Dies Non</span>
                          <span className="fw-bold text-dark">{selectedEvaluationCandidate.diesNon ?? selectedEvaluationCandidate.adjustmentDaysDiesNon ?? selectedEvaluationCandidate.adjustment_days_dies_non ?? selectedEvaluationCandidate.adjustedDiesNonDays ?? selectedEvaluationCandidate.diesNonDays ?? 0} Days</span>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">List Classification</span>
                      <span className="badge bg-primary text-white" style={{ fontWeight: 700, padding: "4px 10px", fontSize: "11px" }}>
                        {selectedEvaluationCandidate.listClassification || activeTab}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Committee Status</span>
                      <span className="fw-semibold text-dark">
                        {selectedEvaluationCandidate.suitability || selectedEvaluationCandidate.committeeStatus || "Pending"}
                      </span>
                    </div>

                    <div>
                      <span className="text-secondary font-medium d-block mb-1">Committee Remarks</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {remarks[String(selectedEvaluationCandidate.key || selectedEvaluationCandidate.empId || selectedEvaluationCandidate.id)] || selectedEvaluationCandidate.committeeRemarks || selectedEvaluationCandidate.remarks || "—"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab 2: WR Details */}
                {activeModalTab === "wr" && (
                  <div>
                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "14px" }}>
                      Work Report (WR) Evaluation & Ratings
                    </h6>

                    {selectedEvaluationCandidate.appraisalDetails && Array.isArray(selectedEvaluationCandidate.appraisalDetails) && selectedEvaluationCandidate.appraisalDetails.length > 0 ? (
                      <div className="table-responsive mb-3">
                        <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                          <thead style={{ backgroundColor: "#f1f5f9" }}>
                            <tr>
                              <th className="py-2 px-3" style={{ color: "#334155", fontWeight: 700 }}>Year</th>
                              <th className="py-2 px-3 text-center" style={{ color: "#334155", fontWeight: 700 }}>Rating / Grading</th>
                              <th className="py-2 px-3" style={{ color: "#334155", fontWeight: 700 }}>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedEvaluationCandidate.appraisalDetails.map((item: any, i: number) => (
                              <tr key={i}>
                                <td className="py-2 px-3 fw-semibold text-dark">{item.year || item.appraisalYear || `Year ${i + 1}`}</td>
                                <td className="py-2 px-3 text-center fw-bold text-primary">{item.rating || item.grading || item.score || "—"}</td>
                                <td className="py-2 px-3 text-secondary">{item.remarks || item.appraisalRemarks || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : selectedEvaluationCandidate.appraisalDetails && typeof selectedEvaluationCandidate.appraisalDetails === "object" ? (
                      <div className="table-responsive mb-3">
                        <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                          <thead style={{ backgroundColor: "#f1f5f9" }}>
                            <tr>
                              <th className="py-2 px-3" style={{ color: "#334155", fontWeight: 700 }}>Year</th>
                              <th className="py-2 px-3 text-end" style={{ color: "#334155", fontWeight: 700 }}>Grading</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(selectedEvaluationCandidate.appraisalDetails).map(([yr, val]: [string, any]) => (
                              <tr key={yr}>
                                <td className="py-2 px-3 fw-semibold text-dark">{yr}</td>
                                <td className="py-2 px-3 text-end fw-bold text-primary">{String(val)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="alert alert-light border text-muted small p-3 rounded mb-3">
                        No individual year WR appraisal ratings recorded.
                      </div>
                    )}

                    <div className="row g-3">
                      <div className="col-6">
                        <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                          <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>WR Final Status</div>
                          <div>{getWrBadge(selectedEvaluationCandidate.wrFinalStatus || selectedEvaluationCandidate.wrStatus)}</div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                          <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>WR Remarks</div>
                          <div className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{selectedEvaluationCandidate.wrRemarks || "—"}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab 3: D&AR Details */}
                {activeModalTab === "dar" && (
                  <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">D&AR Status</span>
                      <div>{getDarBadge(selectedEvaluationCandidate)}</div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Case No.</span>
                      <span className="fw-semibold text-dark">
                        {selectedEvaluationCandidate.darDetails?.caseNo || selectedEvaluationCandidate.caseNo || "—"}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Charge Sheet Issued Date</span>
                      <span className="fw-semibold text-dark">
                        {formatDateDMY(selectedEvaluationCandidate.darDetails?.chargeSheetIssuedDate || selectedEvaluationCandidate.chargeSheetIssuedDate)}
                      </span>
                    </div>

                    <div className="pb-2 border-bottom">
                      <span className="text-secondary font-medium d-block mb-1">Penalty Details</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {selectedEvaluationCandidate.darDetails?.penaltyDescription || selectedEvaluationCandidate.penaltyDescription || "—"}
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Date of Action</span>
                      <span className="fw-semibold text-dark">
                        {formatDateDMY(selectedEvaluationCandidate.darDetails?.dateOfAction || selectedEvaluationCandidate.dateOfAction)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Penalty Start Date</span>
                      <span className="fw-semibold text-dark">
                        {formatDateDMY(selectedEvaluationCandidate.darDetails?.penaltyStartDate || selectedEvaluationCandidate.penaltyStartDate)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Penalty End Date</span>
                      <span className="fw-semibold text-dark">
                        {formatDateDMY(selectedEvaluationCandidate.darDetails?.penaltyEndDate || selectedEvaluationCandidate.penaltyEndDate)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Tab 4: Vigilance & PVR */}
                {activeModalTab === "vigilance" && (
                  <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "14px" }}>PVR Details</h6>
                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">PVR Status</span>
                      <div>{getPvrBadge(selectedEvaluationCandidate)}</div>
                    </div>
                    <div className="pb-2 border-bottom">
                      <span className="text-secondary font-medium d-block mb-1">PVR Remarks</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {(selectedEvaluationCandidate?.pvrRemarks && selectedEvaluationCandidate.pvrRemarks !== "—" && selectedEvaluationCandidate.pvrRemarks !== "")
                          ? selectedEvaluationCandidate.pvrRemarks
                          : mapPvrRecord(selectedEvaluationCandidate).pvrRemarks}
                      </div>
                    </div>

                    <h6 className="fw-bold text-dark mt-2 mb-1" style={{ fontSize: "14px" }}>Vigilance Details</h6>
                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Vigilance Status</span>
                      <div>{getVigilanceBadge(selectedEvaluationCandidate)}</div>
                    </div>
                    <div>
                      <span className="text-secondary font-medium d-block mb-1">Vigilance Remarks</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {selectedEvaluationCandidate.vigilanceRemarks || "—"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EXPORT COMMITTEE MEMBERS MODAL */}
      {showExportModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1060 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: "14px" }}>
              <div className="modal-header bg-primary text-white py-3" style={{ borderRadius: "14px 14px 0 0" }}>
                <h5 className="modal-title fs-16 fw-semibold text-white d-flex align-items-center gap-2 mb-0">
                  <i className="ti ti-file-export fs-18"></i>
                  Configure Screening Committee Members for Assessment Sheet
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowExportModal(false)}
                />
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-info py-2 px-3 mb-4 d-flex align-items-center gap-2" style={{ fontSize: "12px", borderRadius: "6px" }}>
                  <i className="ti ti-info-circle fs-16 flex-shrink-0"></i>
                  <span>
                    Search each Committee Member by Employee Number. The Name and Designation will be auto-fetched from the employee master. These members will be printed on the footer of <strong>every page</strong> (without &apos;Member 1/2/3&apos; text), and the table header will be present on each page of both PDF and Excel.
                  </span>
                </div>

                <div className="row g-3">
                  {[0, 1, 2].map((idx) => {
                    const member = committeeMembers[idx];
                    const titleLabel = idx === 0 ? "Committee Member 1 (Chairperson)" : `Committee Member ${idx + 1}`;
                    const isSearching = searchingIndex === idx;

                    return (
                      <div key={idx} className="col-md-4">
                        <div className="card h-100 border shadow-sm" style={{ borderRadius: "8px" }}>
                          <div className="card-header bg-light py-2 px-3 fw-bold text-dark border-bottom d-flex align-items-center justify-content-between" style={{ fontSize: "13px" }}>
                            <span className="d-flex align-items-center">
                              <i className="ti ti-user me-1.5 text-primary"></i>
                              {titleLabel}
                            </span>
                            {member.name ? (
                              <span className="badge bg-success text-white" style={{ fontSize: "10px", padding: "3px 6px" }}>
                                <i className="ti ti-check me-1"></i>Verified
                              </span>
                            ) : null}
                          </div>
                          <div className="card-body p-3 d-flex flex-column gap-2.5">
                            <div>
                              <label className="form-label mb-1 text-secondary small fw-medium">Emp No / ID</label>
                              <div className="input-group input-group-sm">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="e.g. 00001234"
                                  value={member.empNo}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setCommitteeMembers((prev) => {
                                      const updated = [...prev];
                                      updated[idx] = { ...updated[idx], empNo: val };
                                      return updated;
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleSearchMember(idx);
                                    }
                                  }}
                                />
                                <button
                                  className="btn btn-primary d-flex align-items-center justify-content-center"
                                  type="button"
                                  disabled={isSearching}
                                  onClick={() => handleSearchMember(idx)}
                                  title="Search Employee"
                                  style={{ width: "38px" }}
                                >
                                  {isSearching ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                  ) : (
                                    <i className="ti ti-search" />
                                  )}
                                </button>
                              </div>
                            </div>

                            <div>
                              <label className="form-label mb-1 text-secondary small fw-medium">Employee Name</label>
                              <input
                                type="text"
                                className="form-control form-control-sm bg-light"
                                placeholder="Search Emp No to auto-fill"
                                value={member.name}
                                readOnly={true}
                                style={{ cursor: "not-allowed", fontWeight: member.name ? 600 : 400 }}
                              />
                            </div>

                            <div>
                              <label className="form-label mb-1 text-secondary small fw-medium">Designation</label>
                              <input
                                type="text"
                                className="form-control form-control-sm bg-light"
                                placeholder="Auto-filled from search"
                                value={member.designation}
                                readOnly={true}
                                style={{ cursor: "not-allowed" }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="modal-footer bg-light py-2.5 px-4 d-flex justify-content-between align-items-center" style={{ borderRadius: "0 0 14px 14px" }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setShowExportModal(false)}
                >
                  Cancel
                </button>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-inline-flex align-items-center gap-1.5 px-3"
                    onClick={() => {
                      setShowExportModal(false);
                      handleDownloadPDF();
                    }}
                    title="Download assessment sheet as PDF"
                  >
                    <i className="ti ti-file-type-pdf fs-15"></i>
                    <span>Download PDF</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm d-inline-flex align-items-center gap-1.5 px-3"
                    onClick={() => {
                      setShowExportModal(false);
                      handleDownloadCSV();
                    }}
                    title="Download assessment sheet as Excel (.xls)"
                  >
                    <i className="ti ti-file-spreadsheet fs-15"></i>
                    <span>Download Excel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProbationCommitteeReview;
